// Singleton — sql.js (SQLite in WebAssembly) loads once per browser session
let sqlJsInstance = null;
let loadingPromise = null;

const SQLJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.10.2/sql-wasm.js';
const WASM_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.10.2/sql-wasm.wasm';

function injectScript() {
  return new Promise((resolve, reject) => {
    if (window.initSqlJs) return resolve();
    const script = document.createElement('script');
    script.src = SQLJS_CDN;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load sql.js script'));
    document.head.appendChild(script);
  });
}

async function bootSqlJs() {
  await injectScript();
  const SQL = await window.initSqlJs({ locateFile: () => WASM_CDN });
  sqlJsInstance = SQL;
  return SQL;
}

export async function getSqlJs() {
  if (sqlJsInstance) return sqlJsInstance;
  if (!loadingPromise) {
    loadingPromise = bootSqlJs().catch((e) => {
      loadingPromise = null;
      throw e;
    });
  }
  return loadingPromise;
}

/**
 * Run one or more SQL statements against an in-memory SQLite database.
 * If sampleSql is provided, it is executed first to populate the DB.
 *
 * Returns { rows, columns, output, error }
 */
export async function runSql(userSql, sampleSql = '') {
  const SQL = await getSqlJs();
  const db = new SQL.Database();

  try {
    if (sampleSql) {
      db.run(sampleSql);
    }

    const results = db.exec(userSql);

    if (!results.length) {
      return { rows: [], columns: [], output: '(no rows returned)', error: '' };
    }

    const { columns, values } = results[results.length - 1];
    const rows = values;

    // Render as plain-text table
    const colWidths = columns.map((c, i) =>
      Math.max(String(c).length, ...rows.map((r) => String(r[i] ?? 'NULL').length)),
    );
    const separator = colWidths.map((w) => '-'.repeat(w + 2)).join('+');
    const header = columns.map((c, i) => ` ${String(c).padEnd(colWidths[i])} `).join('|');
    const rowLines = rows.map((row) =>
      row.map((cell, i) => ` ${String(cell ?? 'NULL').padEnd(colWidths[i])} `).join('|'),
    );

    const output = [header, separator, ...rowLines, '', `${rows.length} row(s)`].join('\n');

    return { rows, columns, output, error: '' };
  } catch (e) {
    return { rows: [], columns: [], output: '', error: e.message };
  } finally {
    db.close();
  }
}
